import { collection, doc, onSnapshot, orderBy, query, runTransaction, serverTimestamp } from 'firebase/firestore';
import type { Overage, OverageDecision, Quote } from '@gigdriver/domain';
import { planOverageBilling, planOverageRemoval, quoteTotals, validateOverageDecision } from '@gigdriver/domain';
import { getFirestoreInstance } from './firestore';
import { fromFirestore } from './serde';

function decisionsCollection(orgId: string, gigId: string) {
  return collection(getFirestoreInstance(), 'organizations', orgId, 'gigs', gigId, 'overageDecisions');
}

export interface RecordOverageDecisionInput {
  decision: 'BILL' | 'ABSORB';
  // BILL: what to bill the client in total for the overage (defaults to the
  // computed amount in the UI; a manager may lower/raise it).
  billAmount?: number;
  // Required for ABSORB.
  notes?: string;
  // The numbers being decided on (frozen onto the decision).
  overage: Overage;
  decidedBy: string; // auth uid — the firestore rule requires this to match
  // The decision this one replaces, when re-deciding.
  supersedes?: string;
}

export interface RecordOverageDecisionResult {
  decisionId: string;
  // BILL: the change-order quote now carrying the overage line.
  quoteId?: string;
}

// Records a manager's bill-or-absorb decision. For BILL the staged quote line
// and the decision are written in ONE transaction, so a decision can never
// point at a line that doesn't exist (or a line appear with no decision).
// Rejections (no quote, quote out for approval, amount already covered, ...)
// throw before anything is written.
export async function recordOverageDecision(
  orgId: string,
  gigId: string,
  input: RecordOverageDecisionInput,
): Promise<RecordOverageDecisionResult> {
  const { overage } = input;
  if (overage.overageCost <= 0) throw new Error('There is no overage to decide on.');
  const invalid = validateOverageDecision({ decision: input.decision, billAmount: input.billAmount, notes: input.notes });
  if (invalid) throw new Error(invalid);

  const db = getFirestoreInstance();
  const gigRef = doc(db, 'organizations', orgId, 'gigs', gigId);
  const decisionRef = doc(decisionsCollection(orgId, gigId));
  const base = {
    id: decisionRef.id,
    orgId,
    gigId,
    decision: input.decision,
    baselineCost: overage.baselineCost,
    actualCost: overage.actualCost,
    overageCost: overage.overageCost,
    marginPct: overage.marginPct,
    billAmount: input.decision === 'BILL' ? Math.round((input.billAmount ?? 0) * 100) / 100 : 0,
    breakdown: overage.breakdown,
    notes: input.notes?.trim() || null,
    supersedes: input.supersedes ?? null,
    decidedBy: input.decidedBy,
    decidedAt: serverTimestamp(),
  };

  return runTransaction(db, async tx => {
    const gigSnap = await tx.get(gigRef);
    const activeQuoteId = gigSnap.exists() ? (gigSnap.data().activeQuoteId as string | undefined) : undefined;
    const quoteRef = activeQuoteId ? doc(db, 'organizations', orgId, 'gigs', gigId, 'quotes', activeQuoteId) : undefined;
    const quoteSnap = quoteRef ? await tx.get(quoteRef) : undefined;
    const quote = quoteSnap?.exists() ? fromFirestore<Quote>(quoteSnap.data()) : null;

    if (input.decision === 'ABSORB') {
      // A staged-but-unsent overage line on the open change order comes off.
      const kept = planOverageRemoval(quote);
      if (quoteRef && quote && kept) {
        tx.update(quoteRef, { lineItems: kept, ...quoteTotals(kept, quote.taxRate), updatedAt: serverTimestamp() });
      }
      tx.set(decisionRef, { ...base, billedQuoteId: null, billedLineId: null });
      return { decisionId: decisionRef.id };
    }

    const lineId = doc(decisionsCollection(orgId, gigId)).id; // unique id for the line
    const plan = planOverageBilling(quote, lineId, decisionRef.id, base.billAmount, overage.breakdown);
    if (!plan.ok) throw new Error(plan.reason);
    if (!quote || !quoteRef) throw new Error('Quote not found.');

    const totals = quoteTotals(plan.lineItems, quote.taxRate);
    let quoteId = quote.id;
    if (plan.mode === 'NEW_CHANGE_ORDER') {
      const coRef = doc(collection(db, 'organizations', orgId, 'gigs', gigId, 'quotes'));
      quoteId = coRef.id;
      tx.set(coRef, {
        id: coRef.id,
        orgId,
        gigId,
        clientId: quote.clientId,
        state: 'DRAFT',
        currency: quote.currency,
        lineItems: plan.lineItems,
        taxRate: quote.taxRate ?? null,
        depositAmount: quote.depositAmount ?? null,
        ...totals,
        parentQuoteId: quote.id,
        changeOrderNumber: (quote.changeOrderNumber ?? 0) + 1,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      tx.update(gigRef, { activeQuoteId: coRef.id, updatedAt: serverTimestamp() });
    } else {
      tx.update(quoteRef, { lineItems: plan.lineItems, ...totals, updatedAt: serverTimestamp() });
    }
    tx.set(decisionRef, { ...base, billedQuoteId: quoteId, billedLineId: plan.line.id });
    return { decisionId: decisionRef.id, quoteId };
  });
}

// Every decision for a gig, oldest first (coordinator+; the append-only
// trail). Use currentDecision()/overageStatus() from the domain to interpret.
export function watchOverageDecisions(
  orgId: string,
  gigId: string,
  onData: (decisions: OverageDecision[]) => void,
  onError?: (err: unknown) => void,
): () => void {
  const q = query(decisionsCollection(orgId, gigId), orderBy('decidedAt', 'asc'));
  return onSnapshot(
    q,
    snap =>
      onData(
        snap.docs.map(d => {
          // 'estimate': a just-written decision has a pending server
          // timestamp; without this its decidedAt would be null until the
          // write round-trips. Optional fields are stored as null — the
          // domain type models "absent" as undefined.
          const data = fromFirestore<Record<string, unknown>>(d.data({ serverTimestamps: 'estimate' }));
          for (const k of Object.keys(data)) if (data[k] === null) delete data[k];
          return data as unknown as OverageDecision;
        }),
      ),
    err => onError?.(err),
  );
}
